import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HistoryQuestionsList from '../components/historyquestions/HistoryQuestionsList';
import HistoryQuestionsModal from '../components/historyquestions/HistoryQuestionsModal';
import AnswersModal from '../components/historyquestions/AnswersModal';
import { historyQuestionsService } from '../services/historyQuestionsService';
import { toast } from 'react-toastify';
import Spinner from '../components/common/Spinner';
import { FaPlus, FaQuestionCircle } from 'react-icons/fa';

const HistoryQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  // Fetch all questions
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await historyQuestionsService.getAllQuestions();
      const sortedQuestions = (response.data || []).sort((a, b) => a.position - b.position);
      setQuestions(sortedQuestions);
    } catch (error) {
      toast.error('Failed to load questions');
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleCreateQuestion = () => {
    setSelectedQuestion(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditQuestion = (question) => {
    setSelectedQuestion(question);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewAnswers = (question) => {
    setSelectedQuestion(question);
    setShowAnswersModal(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        await historyQuestionsService.deleteQuestion(questionId);
        toast.success('Question deleted successfully');
        fetchQuestions();
      } catch (error) {
        toast.error('Failed to delete question');
        console.error('Error deleting question:', error);
      }
    }
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      if (modalMode === 'create') {
        await historyQuestionsService.createQuestion(questionData);
        toast.success('Question created successfully');
      } else {
        await historyQuestionsService.updateQuestion(selectedQuestion.id, questionData);
        toast.success('Question updated successfully');
      }
      setShowModal(false);
      fetchQuestions();
    } catch (error) {
      toast.error(modalMode === 'create' ? 'Failed to create question' : 'Failed to update question');
      console.error('Error saving question:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                <FaQuestionCircle className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">History Questions</h1>
                <p className="text-sm text-gray-600">Manage patient history questions</p>
              </div>
            </div>
            <button
              onClick={handleCreateQuestion}
              className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all font-semibold shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
            >
              <FaPlus size={14} /> Add New Question
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="px-3 py-1 bg-gray-100 rounded-full font-medium">
              Total Questions: {questions.length}
            </span>
          </div>
        </div>

        {/* Content */}
        <HistoryQuestionsList
          questions={questions}
          onEdit={handleEditQuestion}
          onDelete={handleDeleteQuestion}
          onViewAnswers={handleViewAnswers}
        />
      </div>

      {/* Modals */}
      {showModal && (
        <HistoryQuestionsModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveQuestion}
          question={selectedQuestion}
          mode={modalMode}
        />
      )}

      {showAnswersModal && selectedQuestion && (
        <AnswersModal
          isOpen={showAnswersModal}
          onClose={() => setShowAnswersModal(false)}
          question={selectedQuestion}
        />
      )}
    </Layout>
  );
};

export default HistoryQuestionsPage;
